import React from 'react';
import { useData } from '../context/DataContext';
import ImportExcel from './ImportExcel';
import DashboardCharts from './DashboardCharts';

const Dashboard = () => {
  const { kpi, loading } = useData();

  if (loading) {
    return <div className="loading">Chargement des données...</div>;
  }

  const kpiItems = [
    { label: 'Clients', value: kpi.totalClients || 0 },
    { label: 'Devis émis', value: kpi.totalDevis || 0 },
    { label: 'Devis acceptés', value: kpi.devisAcceptes || 0 },
    { label: "Taux d'acceptation", value: (kpi.tauxAcceptation || 0).toFixed(1) + '%' },
    { label: 'CA facturé HT', value: (kpi.caFactureHT || 0).toFixed(0) + ' €' },
    { label: 'Coûts fournisseurs', value: (kpi.totalCoutsHT || 0).toFixed(0) + ' €' },
    { label: 'Marge brute', value: (kpi.margeBrute || 0).toFixed(0) + ' €' },
    { label: 'Marge brute %', value: (kpi.margeBrutePct || 0).toFixed(1) + '%' },
    { label: 'Charges de structure', value: (kpi.coutsStructureHT || 0).toFixed(0) + ' €' },
    { label: 'Résultat net', value: (kpi.resultatNet || 0).toFixed(0) + ' €' },
    { label: 'Reste à encaisser TTC', value: (kpi.resteAEncaisser || 0).toFixed(0) + ' €' },
  ];

  const euro = (n) =>
    (Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  const tvaNette = kpi.tvaNette || 0;

  return (
    <div>
      <h2>📊 Tableau de bord</h2>
      
      <ImportExcel />

      <div className="kpi-grid">
        {kpiItems.map((item, index) => (
          <div key={index} className="kpi-card">
            <strong>{item.label}</strong>
            <span>{item.value}</span>
          </div>
        ))}
      </div>

      <section className="top-section" aria-label="Rentabilité globale">
        <h3>💶 Rentabilité globale (charges de structure comprises)</h3>
        <div className="tva-bloc">
          <div className="tva-bloc-item">
            <span className="tva-bloc-label">CA facturé HT</span>
            <strong className="tva-bloc-value">{euro(kpi.caFactureHT)}</strong>
            <span className="tva-bloc-sub">factures émises</span>
          </div>
          <div className="tva-bloc-item">
            <span className="tva-bloc-label">Marge brute</span>
            <strong className="tva-bloc-value">{euro(kpi.margeBrute)}</strong>
            <span className="tva-bloc-sub">
              CA − coûts fournisseurs ({kpi.margeBrutePct != null ? kpi.margeBrutePct.toFixed(1) : '0.0'} %)
            </span>
          </div>
          <div className="tva-bloc-item">
            <span className="tva-bloc-label">− Coûts de structure</span>
            <strong className="tva-bloc-value" style={{ color: 'var(--color-danger)' }}>{euro(kpi.coutsStructureHT)}</strong>
            <span className="tva-bloc-sub">charges générales (loyer, assurances…)</span>
          </div>
          <div className="tva-bloc-item">
            <span className="tva-bloc-label">Résultat net</span>
            <strong
              className="tva-bloc-value"
              style={{ color: (kpi.resultatNet || 0) >= 0 ? 'var(--color-success)' : 'var(--color-error)' }}
            >
              {euro(kpi.resultatNet)}
            </strong>
            <span className="tva-bloc-sub">
              {kpi.resultatNetPct != null ? kpi.resultatNetPct.toFixed(1) : '0.0'} % du CA
            </span>
          </div>
        </div>
      </section>

      <section className="top-section" aria-label="TVA collectée vs déductible">
        <h3>🧾 TVA — collectée vs déductible</h3>
        <div className="tva-bloc">
          <div className="tva-bloc-item">
            <span className="tva-bloc-label">TVA collectée</span>
            <strong className="tva-bloc-value">{euro(kpi.tvaCollectee)}</strong>
            <span className="tva-bloc-sub">sur factures émises</span>
          </div>
          <div className="tva-bloc-item">
            <span className="tva-bloc-label">TVA déductible</span>
            <strong className="tva-bloc-value">{euro(kpi.tvaDeductible)}</strong>
            <span className="tva-bloc-sub">sur achats &amp; sous-traitance</span>
          </div>
          <div className="tva-bloc-item">
            <span className="tva-bloc-label">TVA nette</span>
            <strong
              className="tva-bloc-value"
              style={{ color: tvaNette >= 0 ? 'var(--color-success)' : 'var(--color-error)' }}
            >
              {euro(tvaNette)}
            </strong>
            <span className="tva-bloc-sub">
              {tvaNette >= 0 ? 'à reverser à l’État' : 'crédit de TVA en votre faveur'}
            </span>
          </div>
        </div>
        <p className="preview-note">
          Détail par mois, chantier et client dans l'onglet « TVA ».
        </p>
      </section>

      <DashboardCharts />

      <section className="top-section" aria-label="Top 10 chantiers">
        <h3>🏆 Top 10 chantiers (par CA facturé)</h3>
        <table className="top-table" role="table">
          <thead>
            <tr>
              <th>Rang</th>
              <th>Chantier</th>
              <th>CA facturé HT</th>
              <th>Coûts HT</th>
              <th>Marge brute</th>
            </tr>
          </thead>
          <tbody>
            {(kpi.topChantiers || []).map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{item.chantier}</td>
                <td>{item.ca.toFixed(2)} €</td>
                <td>{item.couts.toFixed(2)} €</td>
                <td className={item.marge >= 0 ? 'positive' : 'negative'}>
                  {item.marge.toFixed(2)} €
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="top-section" aria-label="Top 10 clients">
        <h3>🏆 Top 10 clients (par CA facturé)</h3>
        <table className="top-table" role="table">
          <thead>
            <tr>
              <th>Rang</th>
              <th>Client</th>
              <th>CA facturé HT</th>
              <th>Coûts HT</th>
              <th>Marge brute</th>
            </tr>
          </thead>
          <tbody>
            {(kpi.topClients || []).map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{item.client}</td>
                <td>{item.ca.toFixed(2)} €</td>
                <td>{item.couts.toFixed(2)} €</td>
                <td className={item.marge >= 0 ? 'positive' : 'negative'}>
                  {item.marge.toFixed(2)} €
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="top-section" aria-label="Devis par statut">
        <h3>📋 Devis par statut</h3>
        <table className="top-table" role="table">
          <thead>
            <tr>
              <th>Statut</th>
              <th>Nombre</th>
              <th>Montant HT</th>
            </tr>
          </thead>
          <tbody>
            {(kpi.devisParStatut || []).map((item, index) => (
              <tr key={index}>
                <td>
                  <span className={`badge badge-${item.statut.toLowerCase().replace(' ', '-')}`}>
                    {item.statut}
                  </span>
                </td>
                <td>{item.nb}</td>
                <td>{item.montantHT.toFixed(2)} €</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default Dashboard;